import {useContext, useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {Loading} from "~/components";
import {authActions} from "~/reduxs/Auth/authSlice";
import {AppContext} from "~/context/AppProvider";
import {apiError, handleRequest} from "~/utils";
import {authApi} from "~/api";
import {globalNavigate} from "~/routes/GlobalHistory";

// Cache cấp module: cấu hình client + script GIS chỉ tải 1 lần dù nút xuất hiện
// ở cả Login lẫn Register.
let configPromise = null;
let gsiScriptPromise = null;

function loadConfig() {
	if (!configPromise) {
		// Payload không có key 'data' nên bị Response::api bọc 1 lớp: {data:{enabled, client_id}}.
		configPromise = authApi.googleClient().then((r) => r?.data || null).catch(() => null);
	}
	return configPromise;
}

function loadGsiScript() {
	if (window.google?.accounts?.id) return Promise.resolve();
	if (!gsiScriptPromise) {
		gsiScriptPromise = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = 'https://accounts.google.com/gsi/client';
			script.async = true;
			script.defer = true;
			script.onload = resolve;
			script.onerror = () => { gsiScriptPromise = null; reject(new Error('GIS load failed')); };
			document.head.appendChild(script);
		});
	}
	return gsiScriptPromise;
}

/**
 * Nút "Đăng nhập bằng Google" (Google Identity Services — nút chính chủ do Google render).
 * Tự ẩn khi BE chưa cấu hình GOOGLE_CLIENT_ID hoặc không tải được script Google.
 * Nhận credential (ID token) → POST api/auth/google → xử lý response GIỐNG login thường.
 *
 * `text`: 'signin_with' (Đăng nhập bằng Google) | 'signup_with' (Đăng ký bằng Google).
 */
function GoogleLoginButton({text = 'signin_with'}) {

	const dispatch = useDispatch();

	const {setUserLogin} = useContext(AppContext);

	const containerRef = useRef(null);

	const [ready, setReady] = useState(false);
	const [loading, setLoading] = useState(false);

	// Callback giữ trong ref để useEffect init GIS không phải phụ thuộc closure mỗi render.
	const handleCredentialRef = useRef(null);

	handleCredentialRef.current = async (gsiResponse) => {
		if (!gsiResponse?.credential) return;

		setLoading(true);

		const [error, response] = await handleRequest(authApi.googleLogin(gsiResponse.credential));

		const message = apiError('Đăng nhập bằng Google thất bại', error, response);

		if (!message) {
			// Đồng bộ với AuthLoginForm.onSubmit — response cùng cấu trúc login.
			localStorage.setItem('access_token', JSON.stringify({
				accessToken : response.accessToken,
				expires     : response.expires,
			}));
			localStorage.setItem('reload_token', response.refreshToken);
			dispatch(authActions.loginSuccess(response.data.user));
			setUserLogin(true);
			globalNavigate(process.env.REACT_APP_HOMEPAGE + "/");
			return;
		}

		setLoading(false);
	};

	useEffect(() => {
		let cancelled = false;

		(async () => {
			const config = await loadConfig();

			if (cancelled || !config?.enabled || !config?.client_id) return;

			try { await loadGsiScript(); } catch (e) { return; }

			if (cancelled || !containerRef.current || !window.google?.accounts?.id) return;

			window.google.accounts.id.initialize({
				client_id: config.client_id,
				callback: (gsiResponse) => handleCredentialRef.current?.(gsiResponse),
			});

			// GIS nhận width dạng px (tối đa 400) — bám theo bề rộng khung form.
			const width = Math.min(Math.max(containerRef.current.offsetWidth || 320, 200), 400);

			window.google.accounts.id.renderButton(containerRef.current, {
				type: 'standard',
				theme: 'outline',
				size: 'large',
				text,
				locale: 'vi',
				logo_alignment: 'center',
				width,
			});

			setReady(true);
		})();

		return () => { cancelled = true; };
	}, [text]);

	// Chưa sẵn sàng (đang tải / BE chưa bật) → không chiếm chỗ, form không đổi bố cục.
	return (
		<div style={{display: ready ? 'block' : 'none', marginBottom: 18}}>
			<div style={{display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0 14px', color: '#9aa5b1', fontSize: 13}}>
				<span style={{flex: 1, height: 1, background: '#e3e9f0'}}></span>
				hoặc
				<span style={{flex: 1, height: 1, background: '#e3e9f0'}}></span>
			</div>
			<div style={{position: 'relative'}}>
				{loading && <Loading/>}
				<div ref={containerRef} style={{display: 'flex', justifyContent: 'center'}}></div>
			</div>
		</div>
	);
}

export default GoogleLoginButton;
